/* William Painter — Rx bundle grouped display in Rebuy SmartCart
 * The Rx prescription components are separate cart lines (each a lab-routable variant,
 * sharing a _rx_bundle token; locked together by rx-cart-lock.js). This presents them as
 * ONE visual "Prescription Lenses" group in the Rebuy drawer: a header above the components,
 * the per-component prices kept as the breakdown, and a single remove control (removing it
 * clears the whole bundle via the cart lock). Rebuy re-renders its drawer, so a MutationObserver
 * re-applies after each render. A line belongs to a pair when it carries the `_rx_bundle` property
 * (Rebuy encodes it as a class), whatever its product: the component products and the frame twins
 * (the-hook-rx, the-wrap-rx, ...) alike. See PRESCRIPTION-LENSES-ROADMAP.md and
 * BUILD-YOUR-OWN-PAIR-ROADMAP.md.
 */
(function () {
  'use strict';
  // Every line of a pair carries _rx_bundle, so key on the property, not the product handle.
  var RX = '.rebuy-cart__flyout-item[class*="property-_rx_bundle:"]';

  // Scoped styles for the grouped block.
  function injectCSS() {
    if (document.getElementById('rx-group-css')) return;
    var s = document.createElement('style');
    s.id = 'rx-group-css';
    s.textContent =
      // Group header
      '.rx-group-header{display:flex;justify-content:space-between;align-items:center;font-weight:700;font-size:13px;letter-spacing:.04em;text-transform:uppercase;padding:14px 0 8px;margin-top:6px;border-top:1px solid #eee;}' +
      '.rx-group-header .rx-group-remove{font-weight:500;font-size:12px;letter-spacing:0;text-transform:none;text-decoration:underline;cursor:pointer;color:#6e6e73;background:none;border:none;padding:0;}' +
      // Collapse each component into a tight single-line row. Kill the empty media box, the
      // per-line qty/remove controls, and the verbose spec properties (those stay on the line
      // item, so the order/lab still receives them — we only hide them in the drawer).
      '.rebuy-cart__flyout-item.rx-line{display:block!important;border:none!important;box-shadow:none!important;margin:0!important;padding:5px 0!important;min-height:0!important;}' +
      '.rebuy-cart__flyout-item.rx-line .rebuy-cart__flyout-item-media,' +
      '.rebuy-cart__flyout-item.rx-line .rebuy-cart__flyout-item-remove,' +
      '.rebuy-cart__flyout-item.rx-line .rebuy-cart__flyout-item-quantity,' +
      '.rebuy-cart__flyout-item.rx-line .rebuy-cart__flyout-item-properties{display:none!important;}' +
      '.rebuy-cart__flyout-item.rx-line .rebuy-cart__flyout-item-info{display:flex!important;flex-wrap:wrap;align-items:baseline;width:100%;gap:0 10px;margin:0!important;padding:0!important;}' +
      // Component rows: the variant title is the label ("High Index 1.67"); product title is noise.
      '.rebuy-cart__flyout-item.rx-line .rebuy-cart__flyout-item-product-title{display:none;}' +
      '.rebuy-cart__flyout-item.rx-line .rebuy-cart__flyout-item-variant-title{flex:1;font-size:14px;line-height:1.3;color:#1d1d1f;margin:0!important;}' +
      '.rebuy-cart__flyout-item.rx-line .rebuy-cart__flyout-item-price{margin-left:auto;white-space:nowrap;}' +
      '.rebuy-cart__flyout-item.rx-line .rebuy-cart__flyout-item-price *{font-size:14px!important;font-weight:400!important;color:#1d1d1f!important;}' +
      // Frame lead row (new pair): bold frame name leads, price on the same row, variant muted beneath.
      '.rebuy-cart__flyout-item.rx-line.rx-lead-frame{padding-bottom:8px!important;}' +
      '.rebuy-cart__flyout-item.rx-line.rx-lead-frame .rebuy-cart__flyout-item-product-title{display:block;flex:1 1 auto;order:1;font-size:15px;font-weight:600;line-height:1.3;color:#1d1d1f;text-decoration:none;margin:0!important;}' +
      '.rebuy-cart__flyout-item.rx-line.rx-lead-frame .rebuy-cart__flyout-item-price{order:2;}' +
      '.rebuy-cart__flyout-item.rx-line.rx-lead-frame .rebuy-cart__flyout-item-price *{font-weight:600!important;}' +
      '.rebuy-cart__flyout-item.rx-line.rx-lead-frame .rebuy-cart__flyout-item-variant-title{flex:1 1 100%;order:3;font-size:12px;color:#6e6e73;}' +
      // Lead line WITH a frame photo: show the thumbnail (injected from the _rx_frame_image
      // property via /cart.js) and lay the row out media | info, like a normal cart line.
      '.rebuy-cart__flyout-item.rx-line.rx-has-frame-img{display:flex!important;align-items:flex-start;gap:12px;}' +
      '.rebuy-cart__flyout-item.rx-line.rx-has-frame-img .rebuy-cart__flyout-item-media{display:block!important;flex:0 0 54px;width:54px;height:auto!important;min-height:0!important;opacity:1;transform:none;margin:0;}' +
      '.rebuy-cart__flyout-item.rx-line.rx-has-frame-img .rebuy-cart__flyout-item-media a{display:block;}' +
      '.rebuy-cart__flyout-item.rx-line.rx-has-frame-img .rebuy-cart__flyout-item-media img{width:54px;height:54px;object-fit:contain;border-radius:8px;background:#f5f5f7;display:block;}' +
      '.rebuy-cart__flyout-item.rx-line.rx-has-frame-img .rebuy-cart__flyout-item-info{flex:1;}' +
      // Rebuy draws its stepper/remove in a few wrappers depending on layout; none belong inside a card.
      '.rebuy-cart .rx-card .rebuy-cart__flyout-item-quantity,.rebuy-cart .rx-card .rebuy-cart__flyout-item-quantity-widget,.rebuy-cart .rx-card .rebuy-cart__flyout-item-remove,.rebuy-cart .rx-card .rebuy-cart__flyout-item-actions{display:none!important;}' +
      // Don't let the title/thumbnail link out to the bare component product page.
      '.rebuy-cart__flyout-item.rx-line .rebuy-cart__flyout-item-product-title,' +
      '.rebuy-cart__flyout-item.rx-line .rebuy-cart__flyout-item-media a{pointer-events:none;cursor:default;}' +
      // Clear and Blue Light Blocker swatches are near-white; wherever Rebuy shows one (before the
      // lines are grouped, or if grouping can't run), give it a soft grey tile so it reads as a lens.
      '.rebuy-cart__flyout-item-media img[src*="byo-lens-clear"],.rebuy-cart__flyout-item-media img[src*="byo-lens-bluelight"]{background:#eceef1;border-radius:8px;box-shadow:inset 0 0 0 1px rgba(0,0,0,.06);}' +
      // Combined bundle total
      '.rx-group-total{display:flex;justify-content:space-between;align-items:center;padding:10px 0 4px;margin-top:4px;border-top:1px solid #eee;}' +
      '.rx-group-total .rx-group-total-label{text-transform:uppercase;letter-spacing:.03em;font-size:12px;color:#6e6e73;}' +
      '.rx-group-total .rx-group-total-amount{font-size:14px;font-weight:600;color:#1d1d1f;}' +
      // The bundle lives in one bordered "card". Moving the lines into this block also lifts them
      // out of the flex <ul> (display:flex;gap:16px), so the rows sit tight instead of 16px apart.
      '.rebuy-cart .rx-card{border:1px solid #e3e3e3;border-radius:12px;padding:12px 16px;margin:12px 0;background:#fff;}' +
      '.rebuy-cart .rx-card .rx-group-header{border-top:none;margin-top:0;padding:2px 0 8px;}' +
      '.rebuy-cart .rx-card .rebuy-cart__flyout-item.rx-line{padding:6px 0!important;}' +
      '.rebuy-cart .rx-card .rebuy-cart__flyout-item.rx-line.rx-lead-frame{border-bottom:1px solid #f0f0f0;padding-bottom:10px!important;margin-bottom:2px;}' +
      '.rebuy-cart .rx-card .rx-group-total{padding:8px 0 2px;margin-top:4px;}' +
      // Never let Rebuy recommend the bare Rx component products as an upsell — adding one alone
      // would create an orphan lab-routed line with no prescription. (Primary exclusion is the
      // exclude_rebuy tag in the Rebuy dashboard; this is the client-side backstop.) Rebuy builds tag
      // classes by lowercasing and dropping hyphens, so the rx-component tag arrives as tag-rxcomponent.
      '.rebuy-product-block.tag-rxcomponent,.rebuy-product-block.tag-exclude_rebuy{display:none!important;}';
    document.head.appendChild(s);
  }

  // Rebuy encodes every line-item property as a class on the line — including the hidden
  // underscore ones — e.g. "property-_rx_bundle:rxt_ab12". That's how we recover which bundle a
  // line belongs to (the value isn't rendered as visible text), so multiple pairs in one cart can
  // each become their own card. Rebuy lowercases the value; our real tokens are already lowercase.
  function tokenOf(li) {
    var m = (li.className || '').match(/property-_rx_bundle:(\S+)/);
    return m ? m[1] : 'rx';
  }
  // The frame line of a new pair carries _frame_handle; the legacy rx-frame-base product is kept
  // for carts built before the frame twins shipped.
  function isFrameLine(li) {
    return /(^|\s)property-_frame_handle:/.test(li.className) || li.classList.contains('product-rx-frame-base');
  }
  // The anchor line (the first line the builder adds) carries the pair-level properties.
  function isAnchorLine(li) {
    return /(^|\s)property-_(lens_program|flow):/.test(li.className);
  }
  // Lens program straight from the anchor's class, so the header is right on the first paint.
  function programOf(lines) {
    for (var i = 0; i < lines.length; i++) {
      var m = (lines[i].className || '').match(/(?:^|\s)property-_lens_program:(\S+)/);
      if (m) return m[1];
    }
    return null;
  }

  // Frame photos travel on each bundle's anchor line as a hidden `_rx_frame_image` property.
  // Underscore props aren't rendered as text, so we pull them from /cart.js and paint each onto
  // its own bundle's lead line (keyed by token). Cached, with a background refresh.
  var frameImgByToken = null, programByToken = null, fetching = false;
  // Only trust Shopify CDN image URLs (the property is client-settable, so don't paint arbitrary URLs).
  function safeImg(url) {
    return typeof url === 'string' && /^(https:)?\/\/[^\/]*cdn\.shopify(cloud)?\.com\//i.test(url) ? url : null;
  }
  function applyFrameImgs(root, map) {
    if (!map) return;
    Array.prototype.forEach.call(root.querySelectorAll('.rx-lead.rx-line'), function (line) {
      var url = safeImg(map[tokenOf(line)]);
      if (!url) return;
      var media = line.querySelector('.rebuy-cart__flyout-item-media');
      if (!media) return;
      var img = media.querySelector('img') || media.appendChild(document.createElement('img'));
      if (img.getAttribute('src') !== url) {
        img.src = url;
        img.removeAttribute('srcset'); img.removeAttribute('data-src'); img.removeAttribute('data-srcset');
        img.setAttribute('alt', 'Selected frame');
      }
      line.classList.add('rx-has-frame-img');
    });
  }
  // Each card's header names the lens program: nonprescription bundles aren't "Prescription Lenses".
  function applyTitles(root, map) {
    if (!map) return;
    Array.prototype.forEach.call(root.querySelectorAll('.rx-card [data-rx-title]'), function (el) {
      var card = el.closest('.rx-card');
      var program = programOf(card ? card.querySelectorAll('.rx-line') : []) || map[el.getAttribute('data-rx-title')];
      el.textContent = program === 'nonrx' ? 'Custom Lenses' : 'Prescription Lenses';
    });
  }
  function loadFrameImgs(root) {
    if (frameImgByToken) applyFrameImgs(root, frameImgByToken);
    if (programByToken) applyTitles(root, programByToken);
    if (fetching) return;
    fetching = true;
    fetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        fetching = false;
        var map = {}, programs = {};
        (cart.items || []).forEach(function (it) {
          var pr = it.properties || {};
          if (!pr._rx_bundle) return;
          var tok = String(pr._rx_bundle).toLowerCase();
          if (pr._rx_frame_image) { map[tok] = pr._rx_frame_image; }
          if (pr._lens_program) { programs[tok] = pr._lens_program; }
        });
        frameImgByToken = map;
        programByToken = programs;
        applyFrameImgs(root, map);
        applyTitles(root, programs);
      })
      .catch(function () { fetching = false; });
  }

  // The lens finish variant title is "<finish> / <color>". Finishes that come in one color only
  // read as "Clear / Clear" or "Blue Light Blocker / Clear", so show just the finish for those.
  // The finish shows under the name the builder uses (assets/byop-finish-names.js), e.g.
  // "Solid Mirror / Silver" reads "Non Polarized Mirror / Silver". Display only: the variant and
  // its properties keep the catalog name. The catalog text is kept on the element so repeat
  // passes never map an already-mapped title.
  var SINGLE_COLOR_FINISHES = { 'Clear': 1, 'Blue Light Blocker': 1 };
  function finishName(n) {
    var N = window.WPByopFinishNames;
    return (N && N.name(n)) || n;
  }
  function tidyVariantTitles(root) {
    Array.prototype.forEach.call(root.querySelectorAll('.rx-line .rebuy-cart__flyout-item-variant-title'), function (el) {
      var line = el.closest('.rx-line');
      if (!line || isFrameLine(line)) return;      // a frame colorway could also start with "Clear"
      var shown = (el.textContent || '').trim();
      // Rebuy may rewrite the text: a title that isn't the one we set is the new catalog text.
      if (el.getAttribute('data-rx-shown') !== shown) el.setAttribute('data-rx-raw', shown);
      var parts = el.getAttribute('data-rx-raw').split(' / ');
      var out = parts.length === 2 && SINGLE_COLOR_FINISHES[parts[0]] ? parts[0]
        : parts.length === 2 ? finishName(parts[0]) + ' / ' + parts[1] : el.getAttribute('data-rx-raw');
      if (out !== shown) el.textContent = out;
      el.setAttribute('data-rx-shown', out);
    });
  }

  // Build (but don't insert) one bordered card for a single bundle's lines. The card's Remove
  // clears just this bundle: clicking a component's native remove triggers rx-cart-lock.js, which
  // removes every line sharing the same _rx_bundle token.
  function buildCard(groupLines, tok) {
    var card = document.createElement('div');
    card.className = 'rx-card';
    var program = programOf(groupLines) || (programByToken && programByToken[tok]);
    var hasFrame = groupLines.some(isFrameLine);

    var header = document.createElement('div');
    header.className = 'rx-group-header';
    header.innerHTML = '<span data-rx-title="' + String(tok).replace(/"/g, '') + '">' + (program === 'nonrx' ? 'Custom Lenses' : 'Prescription Lenses') + '</span><button type="button" class="rx-group-remove" aria-label="' + (hasFrame ? 'Remove this pair' : 'Remove these lenses') + '">Remove</button>';
    header.querySelector('.rx-group-remove').addEventListener('click', function () {
      var rm = card.querySelector('.rebuy-cart__flyout-item-remove');
      if (rm) { rm.click(); }
    });
    card.appendChild(header);

    var sum = 0;
    groupLines.forEach(function (li) {
      var p = li.querySelector('.rebuy-cart__flyout-item-price');
      if (p) { var n = parseFloat((p.textContent || '').replace(/[^0-9.]/g, '')); if (!isNaN(n)) { sum += n; } }
      card.appendChild(li);
    });

    var amount = '$' + (sum % 1 === 0 ? sum.toLocaleString('en-US') : sum.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }));
    var total = document.createElement('div');
    total.className = 'rx-group-total';
    total.innerHTML = '<span class="rx-group-total-label">' + (hasFrame ? 'Pair total' : 'Lenses total') + '</span><span class="rx-group-total-amount">' + amount + '</span>';
    card.appendChild(total);
    return card;
  }

  function group(root) {
    // Always sweep emptied cards first: removing one bundle makes Rebuy delete just those line
    // elements (a partial update), leaving that bundle's card behind with nothing in it. The other
    // bundle's lines stay tagged, so the rebuild below would early-return and the husk would linger.
    Array.prototype.forEach.call(root.querySelectorAll('.rx-card'), function (c) { if (!c.querySelector('.rx-line')) { c.remove(); } });

    var lines = root.querySelectorAll(RX);
    if (!lines.length) return;
    // Idempotency is keyed on the CURRENT lines, NOT on header presence. Rebuy re-renders
    // its drawer a beat after open (loading recommendations/widgets), swapping the line
    // elements for fresh, untagged ones. The old check bailed because a now-stale header
    // still sat in the DOM, so those re-rendered lines never got grouped — which is exactly
    // why the bundle showed ungrouped (qty steppers + per-line removes) in the cart.
    // Fix: only act when untagged Rx lines exist, and when they do, rebuild cleanly —
    // drop any stale header(s), retag every line, re-insert one header before the first.
    var ungrouped = root.querySelectorAll(RX + ':not(.rx-line)');
    if (ungrouped.length === 0) return;

    // Clean slate: drop any stale card/header/total before rebuilding.
    Array.prototype.forEach.call(root.querySelectorAll('.rx-card, .rx-group-header, .rx-group-total'), function (h) { h.remove(); });
    Array.prototype.forEach.call(lines, function (el) { el.classList.add('rx-line'); });

    // Bucket lines by bundle token (first-seen order) so each prescription pair becomes its own
    // card — adding a second pair no longer merges into / removes with the first.
    var ul = lines[0].parentNode;
    var marker = lines[0].previousSibling;           // where the rx group sat among other cart items
    var buckets = {}, order = [];
    Array.prototype.forEach.call(lines, function (li) {
      var tok = tokenOf(li);
      if (!buckets[tok]) { buckets[tok] = []; order.push(tok); }
      buckets[tok].push(li);
    });

    var insertAfter = marker;
    order.forEach(function (tok) {
      var groupLines = buckets[tok];
      // Lead row: the frame line when the pair has one (new pair), else the anchor line (re-lens).
      var lead = -1, i;
      for (i = 0; i < groupLines.length && lead < 0; i++) { if (isFrameLine(groupLines[i])) lead = i; }
      for (i = 0; i < groupLines.length && lead < 0; i++) { if (isAnchorLine(groupLines[i])) lead = i; }
      if (lead > 0) groupLines.unshift(groupLines.splice(lead, 1)[0]);
      var fbLine = groupLines[0];
      if (fbLine) fbLine.classList.add('rx-lead');
      // A frame lead shows the chosen frame name (carried in the "Frame" property), not the twin's title.
      if (fbLine && isFrameLine(fbLine)) {
        fbLine.classList.add('rx-lead-frame');
        var frameVal = fbLine.querySelector('.property-key-frame .rebuy-cart__flyout-item-property-value');
        var titleEl = fbLine.querySelector('.rebuy-cart__flyout-item-product-title');
        if (frameVal && titleEl && frameVal.textContent.trim()) { titleEl.textContent = frameVal.textContent.trim(); }
      }
      var card = buildCard(groupLines, tok);
      ul.insertBefore(card, insertAfter ? insertAfter.nextSibling : ul.firstChild);
      insertAfter = card;                            // keep cards in order, after one another
    });

    // Paint each bundle's frame photo onto its lead line.
    loadFrameImgs(root);
  }

  var cartObserved = false;
  function run() {
    var root = document.querySelector('.rebuy-cart');
    if (!root) return;
    // Rebuy toggles the drawer via a class change (is-visible) and can re-render its items
    // without a body-level childList mutation our main observer would catch — that race left
    // the bundle ungrouped after a hide/show. Watch the cart element's own class so show/hide/
    // re-render reliably re-triggers grouping.
    if (!cartObserved) { cartObserved = true; new MutationObserver(schedule).observe(root, { attributes: true, attributeFilter: ['class'] }); }
    injectCSS();
    group(root);
    tidyVariantTitles(root);   // every pass: Rebuy can rewrite a line's text without replacing it
  }

  // Debounced re-apply: a leading rAF pass for responsiveness PLUS a trailing pass once
  // mutations settle, so we never grab an intermediate render frame and then miss the final one.
  var rafPending = false, trailing = null;
  function schedule() {
    if (!rafPending) { rafPending = true; requestAnimationFrame(function () { rafPending = false; run(); }); }
    clearTimeout(trailing);
    trailing = setTimeout(run, 250);
  }
  function start() {
    new MutationObserver(schedule).observe(document.body, { childList: true, subtree: true });
    ['rebuy:cart.ready', 'rebuy:cart.show', 'rebuy:cart.change', 'rebuy:smartcart.ready', 'rebuy:smartcart.show']
      .forEach(function (ev) { document.addEventListener(ev, schedule); });
    run();
  }
  if (document.readyState !== 'loading') start();
  else document.addEventListener('DOMContentLoaded', start);
})();
